/**
 * Copies text to the clipboard, on plain HTTP too.
 *
 * `navigator.clipboard` exists only in a SECURE CONTEXT - HTTPS or localhost -
 * and this app is deployed over plain HTTP on an internal VM, where the API is
 * simply absent. So the modern path is tried first and the legacy one -
 * select a hidden textarea and `execCommand('copy')` - is the fallback that
 * actually runs in production. `execCommand` is deprecated but universally
 * supported, and it works from a user gesture without a secure context.
 *
 * Resolves `true` when the text was copied.
 */
export async function copyText(text: string): Promise<boolean> {
  if (window.isSecureContext && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Permission refused or document unfocused: fall through to the legacy path.
    }
  }

  return legacyCopy(text);
}

function legacyCopy(text: string): boolean {
  const field = document.createElement('textarea');
  field.value = text;
  // Kept out of view and out of the layout, but still selectable - a
  // `display: none` element cannot hold a selection.
  field.setAttribute('readonly', '');
  field.style.position = 'fixed';
  field.style.top = '-1000px';
  field.style.opacity = '0';

  const previousFocus =
    document.activeElement instanceof HTMLElement ? document.activeElement : null;

  document.body.appendChild(field);
  field.select();

  let copied: boolean;

  try {
    copied = document.execCommand('copy');
  } catch {
    copied = false;
  }

  field.remove();
  previousFocus?.focus();

  return copied;
}
